import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Check, Printer, Share2, Bluetooth, Loader2, Banknote } from "lucide-react";
import { RupeeChip } from "@/components/pos/RupeeChip";
import {
  changeOptions,
  formatINR,
  itemAmount,
  posApi,
  receiptText,
  roundMoney,
} from "@/lib/pos";
import {
  bluetoothSupported,
  printViaBluetooth,
  printViaBrowser,
} from "@/lib/escpos";
import { useAuth } from "@/context/AuthContext";

const QUICK = [50, 100, 200, 500, 2000];

export default function PaymentSheet({ open, onClose, items, total, onCompleted }) {
  const { token } = useAuth();
  const [cash, setCash] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (open) {
      setCash("");
      setSaved(null);
    }
  }, [open]);

  const cashNum = cash === "" ? 0 : roundMoney(cash);
  const change = Math.max(cashNum - roundMoney(total), 0);
  const short = Math.max(roundMoney(total) - cashNum, 0);
  const options = useMemo(() => changeOptions(change), [change]);
  const notes = options[0]?.notes || [];
  const draft = {
    receipt_no: saved?.receipt_no || "DRAFT",
    local_time: saved?.local_time || new Date().toTimeString().slice(0, 5),
    items,
    total: roundMoney(total),
    cash_received: cashNum || roundMoney(total),
    change_given: change,
  };

  const complete = async () => {
    if (roundMoney(total) <= 0) {
      toast.error("Add at least one item before completing");
      return;
    }
    setSaving(true);
    try {
      const txn = await posApi.createTransaction(token, {
        items: items.map((it) => ({
          label: it.label || "",
          price: roundMoney(it.price),
          qty: it.qty || 1,
          sign: it.sign || 1,
        })),
        total: roundMoney(total),
        cash_received: cashNum || roundMoney(total),
        change_given: change,
        denominations: notes.reduce((a, n) => ({ ...a, [n.value]: n.count }), {}),
      });
      setSaved(txn);
      toast.success(`Saved ${txn.receipt_no}`);
      onCompleted?.(txn);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save transaction");
    } finally {
      setSaving(false);
    }
  };

  const btPrint = async () => {
    setPrinting(true);
    try {
      await printViaBluetooth(saved || draft);
      toast.success("Sent to thermal printer");
    } catch (e) {
      toast.error(e?.message || "Bluetooth print failed");
    } finally {
      setPrinting(false);
    }
  };

  const share = () => {
    const text = receiptText(saved || draft);
    if (navigator.share) {
      navigator.share({ title: "Sky Basket Receipt", text }).catch(() => {});
      return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="payment-sheet"
        className="max-w-lg rounded-[4px] p-0 gap-0 font-pos max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="font-black text-slate-900 text-xl flex items-center gap-2">
            <Banknote size={20} className="text-[#15803d]" />
            {saved ? `Receipt ${saved.receipt_no}` : "Checkout"}
          </DialogTitle>
          <DialogDescription className="text-xs font-semibold text-slate-500">
            Enter the cash received to see the change and the notes to hand back.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-5 space-y-4">
          <div className="rounded-[4px] bg-slate-900 text-white p-4 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">
                Bill amount
              </div>
              <div
                data-testid="payment-total"
                className="font-black text-4xl leading-none tabular-nums mt-1"
              >
                {formatINR(total)}
              </div>
            </div>
            <div className="text-right text-white/70 text-xs font-semibold">
              {items.length} item{items.length === 1 ? "" : "s"}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
              Cash received
            </label>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-black text-slate-400 text-2xl">
                  ₹
                </span>
                <input
                  data-testid="cash-received-input"
                  type="number"
                  inputMode="numeric"
                  value={cash}
                  onChange={(e) => setCash(e.target.value)}
                  placeholder="0"
                  className="w-full h-16 pl-10 pr-3 rounded-[4px] border-2 border-slate-300 focus:border-[#299fe0] outline-none font-black text-3xl tabular-nums text-slate-900"
                />
              </div>
              <button
                type="button"
                data-testid="cash-exact-button"
                onClick={() => setCash(String(roundMoney(total)))}
                className="h-16 px-4 rounded-[4px] bg-slate-900 text-white font-bold text-sm uppercase tracking-[0.1em]"
              >
                Exact
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {QUICK.map((q) => (
                <button
                  key={q}
                  type="button"
                  data-testid={`quick-tender-${q}`}
                  onClick={() => setCash(String((cash === "" ? 0 : roundMoney(cash)) + q))}
                  className="px-3 py-2 rounded-[4px] bg-white border-2 border-slate-200 hover:border-slate-900 font-bold text-slate-800 tabular-nums text-sm"
                >
                  +₹{q}
                </button>
              ))}
            </div>
          </div>

          <div
            data-testid="change-amount-card"
            className={`rounded-[4px] p-4 border-2 ${
              short > 0
                ? "bg-[#fef2f2] border-[#b91c1c]"
                : "bg-[#f0fdf4] border-[#15803d]"
            }`}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">
              {short > 0 ? "Still to collect" : "Change to give"}
            </div>
            <div
              data-testid="change-amount"
              className={`font-black text-5xl leading-none tabular-nums mt-1 ${
                short > 0 ? "text-[#b91c1c]" : "text-[#15803d]"
              }`}
            >
              {formatINR(short > 0 ? short : change)}
            </div>
          </div>

          {change > 0 && (
            <div data-testid="denomination-breakdown" className="space-y-2">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
                Give back these notes & coins
              </div>
              {options.map((opt, oi) => (
                <div key={opt.id}>
                  {oi > 0 && (
                    <div className="flex items-center gap-2 my-2">
                      <div className="h-px flex-1 bg-slate-200" />
                      <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                        or
                      </span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                  )}
                  <div
                    data-testid={`change-option-${opt.id}`}
                    className={`rounded-[4px] p-3 border-2 ${
                      oi === 0
                        ? "border-[#15803d] bg-[#f0fdf4]"
                        : "border-slate-200 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-[10px] uppercase tracking-[0.16em] font-black ${
                          oi === 0 ? "text-[#15803d]" : "text-slate-500"
                        }`}
                      >
                        {oi === 0 ? "Recommended · fewest notes" : opt.label}
                      </span>
                      <span className="text-[11px] font-bold text-slate-500 tabular-nums">
                        {opt.notes.reduce((s, n) => s + n.count, 0)} pcs
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-3">
                      {opt.notes.map((n) => (
                        <RupeeChip key={n.value} denom={n} count={n.count} size="sm" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 pt-1">
            {!saved ? (
              <button
                type="button"
                data-testid="complete-transaction-button"
                onClick={complete}
                disabled={saving}
                className="h-16 rounded-[4px] bg-[#15803d] hover:bg-[#166534] text-white font-black text-lg uppercase tracking-[0.1em] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 size={20} className="animate-spin" /> : <Check size={22} strokeWidth={3} />}
                Complete transaction
              </button>
            ) : (
              <button
                type="button"
                data-testid="new-sale-button"
                onClick={onClose}
                className="h-16 rounded-[4px] bg-slate-900 text-white font-black text-lg uppercase tracking-[0.1em]"
              >
                Start new sale
              </button>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="print-browser-button"
                onClick={() => printViaBrowser(saved || draft)}
                className="h-12 rounded-[4px] bg-white border-2 border-slate-300 hover:border-slate-900 font-bold text-slate-800 text-sm flex items-center justify-center gap-2"
              >
                <Printer size={16} /> Print / PDF
              </button>
              <button
                type="button"
                data-testid="share-receipt-button"
                onClick={share}
                className="h-12 rounded-[4px] bg-white border-2 border-slate-300 hover:border-slate-900 font-bold text-slate-800 text-sm flex items-center justify-center gap-2"
              >
                <Share2 size={16} /> Share
              </button>
            </div>

            <button
              type="button"
              data-testid="print-bluetooth-button"
              onClick={btPrint}
              disabled={!bluetoothSupported() || printing}
              title={
                bluetoothSupported()
                  ? "Print on a Bluetooth thermal printer"
                  : "Web Bluetooth is not supported in this browser"
              }
              className="h-12 rounded-[4px] bg-[#0e6ea6] hover:bg-[#0b5c8b] disabled:opacity-40 text-white font-bold text-sm flex items-center justify-center gap-2"
            >
              {printing ? <Loader2 size={16} className="animate-spin" /> : <Bluetooth size={16} />}
              Thermal printer (Bluetooth)
            </button>
          </div>

          {items.length > 0 && (
            <div className="pt-2 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">
                Items
              </div>
              <div className="space-y-1">
                {items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm font-semibold text-slate-700">
                    <span>
                      {it.label || `Item ${i + 1}`}
                      {it.qty > 1 ? ` × ${it.qty}` : ""}
                    </span>
                    <span className="tabular-nums">{formatINR(itemAmount(it))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
